import { useRef, useState } from 'react';
import { toast } from 'sonner';
import EcfBadge, { CERT_BADGE } from '../components/EcfBadge';
import { ENVIRONMENTS } from '../catalogos';
import { ecfService, type EcfConfig, type EcfEnvironment } from '../../../../services/ecf.service';

/**
 * Pestaña "Emisor y certificado": el certificado digital .p12, el ambiente de la DGII contra el
 * que se trabaja, y los datos con los que se identifica la empresa en cada comprobante.
 *
 * Cableada al backend real: subir el certificado lo valida de verdad (abre el .p12, comprueba la
 * contraseña y que traiga llave privada) y devuelve el titular y la vigencia leídos del archivo.
 */

interface EmisorTabProps {
  config: EcfConfig;
  /** Actualiza el estado en memoria mientras se edita el formulario. */
  onChange: (patch: Partial<EcfConfig>) => void;
  /** Recarga desde el servidor tras una operación que cambia datos guardados. */
  onReload: () => Promise<void> | void;
}

/** Fila etiqueta/campo. Se repite tanto en esta pestaña que se extrae aquí. */
function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-start py-4">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      <div className="sm:col-span-2">{children}</div>
    </div>
  );
}

export default function EmisorTab({ config, onChange, onReload }: EmisorTabProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPwd, setCertPwd] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  /**
   * Ambiente tal como está guardado en el servidor. Los botones de ambiente cambian el estado en
   * memoria, pero la prueba de conexión la hace el backend con lo que tiene guardado: sin esto,
   * elegir "Producción" y pulsar Probar conexión autenticaría contra el ambiente anterior y el
   * resultado diría una cosa mientras la pantalla muestra otra.
   */
  const [savedEnv, setSavedEnv] = useState(config.environment);
  const envDirty = config.environment !== savedEnv;

  // Resultado persistente de la última subida: el toast se va solo y el dato de vigencia
  // conviene dejarlo a la vista para confirmar que se subió el certificado correcto.
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const cert = CERT_BADGE[config.cert_status] ?? CERT_BADGE.none;

  const handleUpload = async () => {
    if (!certFile) return;
    setUploading(true);
    setMessage(null);
    try {
      const result = await ecfService.uploadCertificate(certFile, certPwd);

      setCertFile(null);
      setCertPwd('');
      if (fileRef.current) fileRef.current.value = '';
      await onReload();

      if (result.expired) {
        setMessage({
          type: 'error',
          text: `El certificado de ${result.subject} venció el ${result.validTo}. Se guardó, pero no se puede firmar con él.`,
        });
        toast.error('El certificado está vencido');
      } else {
        setMessage({
          type: 'success',
          text: `Certificado válido de ${result.subject}. Vigente del ${result.validFrom} al ${result.validTo}.`,
        });
        toast.success('Certificado cargado y validado');
      }
    } catch (err) {
      const text = err instanceof Error ? err.message : 'No se pudo cargar el certificado';
      setMessage({ type: 'error', text });
      toast.error(text);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('¿Eliminar el certificado digital? No se podrá firmar hasta cargar otro.')) return;
    try {
      await ecfService.deleteCertificate();
      setMessage(null);
      await onReload();
      toast.success('Certificado eliminado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo eliminar el certificado');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await ecfService.updateConfig({
        environment: config.environment,
        emisorName: config.emisor_name,
        emisorRnc: config.emisor_rnc,
        notifyEmail: config.notify_email,
      });
      await onReload();
      setSavedEnv(config.environment);
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const r = await ecfService.testConnection();
      const expira = r.expira ? new Date(r.expira).toLocaleString('es-DO') : 'sin fecha';
      setMessage({
        type: 'success',
        // Sin punto final: en español `toLocaleString` ya termina en "p. m.", y añadir otro
        // dejaba un doble punto a la vista.
        text: `Conexión exitosa con la DGII (${r.ambienteLabel}) en ${(r.ms / 1000).toFixed(1)} s. Token válido hasta ${expira}`,
      });
      toast.success('Conexión exitosa con la DGII');
    } catch (err) {
      const text = err instanceof Error ? err.message : 'No se pudo conectar con la DGII';
      setMessage({ type: 'error', text });
      toast.error('Falló la conexión con la DGII');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {message && (
        <div
          className={`p-4 rounded-lg border ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          <i
            className={`${
              message.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            } mr-2`}
          ></i>
          {message.text}
        </div>
      )}

      {/* Certificado digital */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            <i className="ri-shield-check-line mr-2 text-blue-600"></i>
            Certificado digital
          </h3>
        </div>
        <div className="px-6 divide-y divide-gray-200">
          <Row label="Estado" hint="Titular y vencimiento del certificado cargado">
            <div className="flex items-center gap-3 flex-wrap">
              <EcfBadge variant={cert.variant}>{cert.label}</EcfBadge>
              {config.cert_subject && (
                <span className="text-sm text-gray-900">{config.cert_subject}</span>
              )}
              {config.cert_expires_at && (
                <span className="text-xs text-gray-500">vence {config.cert_expires_at}</span>
              )}
              {config.cert_status !== 'none' && (
                <button
                  onClick={handleDelete}
                  className="text-sm text-red-600 hover:text-red-900 whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1"></i>
                  Eliminar
                </button>
              )}
            </div>
          </Row>

          <Row
            label="Archivo del certificado"
            hint="Formato .p12 / .pfx (o PEM con la llave privada) emitido por una entidad certificadora autorizada"
          >
            <input
              ref={fileRef}
              type="file"
              accept=".p12,.pfx,.pem,.key,application/x-pkcs12"
              onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
              className="text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-gray-50 file:text-sm file:text-gray-700 file:cursor-pointer hover:file:bg-gray-100"
            />
          </Row>

          <Row label="Contraseña del certificado" hint="Clave del archivo .p12">
            <input
              type="password"
              value={certPwd}
              onChange={(e) => setCertPwd(e.target.value)}
              placeholder="••••••••"
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Row>

          <div className="py-4">
            <button
              onClick={handleUpload}
              disabled={uploading || !certFile}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <i
                className={`${uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-2-line'} mr-2`}
              ></i>
              {uploading ? 'Validando...' : 'Cargar certificado'}
            </button>
          </div>
        </div>
      </div>

      {/* Ambiente de la DGII */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            <i className="ri-server-line mr-2 text-blue-600"></i>
            Ambiente de la DGII
          </h3>
        </div>
        <div className="px-6 divide-y divide-gray-200">
          <Row
            label="Ambiente activo"
            hint="Se empieza en pruebas, luego certificación y por último producción"
          >
            <div className="flex flex-wrap gap-2">
              {ENVIRONMENTS.map((e) => (
                <button
                  key={e.value}
                  onClick={() => onChange({ environment: e.value as EcfEnvironment })}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${
                    config.environment === e.value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {e.label}
                </button>
              ))}
            </div>
          </Row>

          <Row
            label="Probar conexión"
            hint="Autentica contra la DGII (semilla → firma → token) con el ambiente y certificado actuales"
          >
            <div>
              <button
                onClick={handleTest}
                disabled={testing || config.cert_status !== 'valid' || envDirty}
                title={
                  config.cert_status === 'none'
                    ? 'Hace falta un certificado cargado'
                    : config.cert_status === 'expired'
                    ? 'El certificado está vencido'
                    : envDirty
                    ? 'Guarda la configuración para probar con el ambiente que seleccionaste'
                    : undefined
                }
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i
                  className={`${testing ? 'ri-loader-4-line animate-spin' : 'ri-plug-line'} mr-2`}
                ></i>
                {testing ? 'Conectando con la DGII...' : 'Probar conexión'}
              </button>
              {envDirty && (
                <p className="text-xs text-amber-700 mt-1.5">
                  <i className="ri-error-warning-line mr-1"></i>
                  Cambiaste el ambiente. Guarda la configuración antes de probar.
                </p>
              )}
            </div>
          </Row>
        </div>
      </div>

      {/* Datos del emisor */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            <i className="ri-building-line mr-2 text-blue-600"></i>
            Datos del emisor
          </h3>
        </div>
        <div className="px-6 divide-y divide-gray-200">
          <Row label="Razón social">
            <input
              type="text"
              value={config.emisor_name ?? ''}
              onChange={(e) => onChange({ emisor_name: e.target.value })}
              placeholder="EMPRESA PRUEBAS SRL"
              className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Row>

          <Row label="RNC">
            <input
              type="text"
              value={config.emisor_rnc ?? ''}
              onChange={(e) => onChange({ emisor_rnc: e.target.value })}
              placeholder="1-01-00000-0"
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Row>

          <Row label="Correo para notificaciones DGII">
            <input
              type="email"
              value={config.notify_email ?? ''}
              onChange={(e) => onChange({ notify_email: e.target.value })}
              placeholder="fiscal@empresa.com"
              className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </Row>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:opacity-50"
        >
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
}
